import type { FC } from "react";

import type { BuilderBlock, BuilderContextInterface, RegisteredComponents } from "@builder.io/sdk-react";
import { Blocks } from "@builder.io/sdk-react";

import { QueryClient, QueryClientProvider } from "@jti/rest-api";
import type { FormInputType } from "@jti/ui/components/ContactForm/ContactForm";
import { ContactForm as UIContactForm } from "@jti/ui/components/ContactForm/ContactForm";

import type { contactFormInputs } from "./ContactForm.builder";

export type ContactFormBuilderProps = BuilderComponentProps<typeof contactFormInputs> & {
  builderBlock: BuilderBlock;
  builderContext: BuilderContextInterface;
  builderComponents: RegisteredComponents;
  attributes: Record<string, string>;
  skipConsentRetrieval?: boolean;
};

const client = new QueryClient();

export const ContactForm: FC<ContactFormBuilderProps> = ({
  title,
  type,
  description,
  disclaimer,
  hiddenFields,
  errorMessage,
  successScreenTitle,
  successScreenButtonLabel,
  successScreenHref,
  isPrefilled,
  isPrefilledDisabled,
  surnameFieldName,
  nameFieldName,
  emailFieldName,
  confirmEmailFieldName,
  phoneNumberFieldName,
  skipConsentRetrieval,
  builderBlock,
  builderContext,
  builderComponents,
}) => {
  const fields: FormInputType[] = (builderBlock?.children ?? [])
    .filter(Boolean)
    .map((child) => child.component?.options)
    .filter(Boolean);

  const preFilledFields = {
    nameFieldName,
    surnameFieldName,
    emailFieldName,
    confirmEmailFieldName,
    phoneNumberFieldName,
  };

  return (
    <QueryClientProvider client={client}>
      <UIContactForm
        title={title}
        type={type}
        description={description}
        hiddenFields={hiddenFields}
        formDisclaimer={disclaimer}
        serverErrorMessage={errorMessage}
        successScreenTitle={successScreenTitle}
        successScreenButtonLabel={successScreenButtonLabel}
        successScreenHref={successScreenHref}
        fields={fields}
        preFilledFields={preFilledFields}
        isPrefilledDisabled={isPrefilledDisabled}
        isPrefilled={isPrefilled}
        skipConsentRetrieval={skipConsentRetrieval}
      >
        {builderBlock?.children?.map((child) => (
          <Blocks
            key={child.id}
            blocks={[child]}
            parent={builderBlock?.id}
            path="children"
            context={builderContext}
            registeredComponents={builderComponents}
          />
        ))}
      </UIContactForm>
    </QueryClientProvider>
  );
};
